import { useCallback, useRef } from 'react';

import type { EventContainer, SourceType, Task } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';

/**
 * Custom hook to fire all the vital stats events for viewing the workspace
 * pages. This takes care of starting and ending the tasks and solves for
 * double firing, firing out of sequence, and handles stale callback
 * references by using refs
 */
export const useMemberPageViewEvents = ({
  taskName,
  source,
}: {
  taskName: Task;
  source: SourceType;
}) => {
  const traceId = useRef<string | undefined>();
  const hasTaskCompleted = useRef(false);

  /**
   * Start task. Will not start a task if it has
   * already been started - to prevent double
   * firing task starts
   */
  const startTask = useCallback(() => {
    if (traceId.current) {
      return;
    }
    traceId.current = Analytics.startTask({
      taskName,
      source,
    });
  }, [source, taskName]);

  /**
   * Succeed task. Will no-op if the task has already
   * been completed. If fired before startTask, it will
   * start the task and then succeed it
   */
  const taskSucceeded = useCallback(
    ({ containers }: { containers?: EventContainer }) => {
      if (hasTaskCompleted.current) {
        return;
      }

      if (!traceId.current) {
        startTask();
      }

      Analytics.taskSucceeded({
        taskName,
        source,
        traceId: traceId.current!,
        containers,
      });

      hasTaskCompleted.current = true;
    },
    [taskName, source, startTask],
  );

  /**
   * Abort task. Will no-op if the task has already
   * been completed. If fired before startTask, it will
   * start the task and then succeed it
   */
  const taskAborted = useCallback(
    async ({
      containers,
      error,
    }: {
      containers?: EventContainer;
      error?: Error;
    }) => {
      if (hasTaskCompleted.current) {
        return;
      }

      if (!traceId.current) {
        startTask();
      }

      Analytics.taskAborted({
        taskName,
        source,
        traceId: traceId.current!,
        containers,
        error,
      });

      hasTaskCompleted.current = true;
    },
    [taskName, source, startTask],
  );

  /**
   * Fail task. Will no-op if the task has already
   * been completed. If fired before startTask, it will
   * start the task and then succeed it
   */
  const taskFailed = useCallback(
    async ({
      containers,
      error,
    }: {
      containers?: EventContainer;
      error?: Error;
    }) => {
      if (hasTaskCompleted.current) {
        return;
      }

      if (!traceId.current) {
        startTask();
      }

      Analytics.taskFailed({
        taskName,
        source,
        traceId: traceId.current!,
        containers,
        error,
      });
      hasTaskCompleted.current = true;
    },
    [taskName, source, startTask],
  );

  return {
    startTask,
    taskSucceeded,
    taskAborted,
    taskFailed,
  };
};
